import { useEffect, useState, useCallback } from "react";
import { toast, Toaster } from "sonner";
import { Check, X, MapPin, Monitor, History, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/pages/admin/AdminLayout";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-gray-200 text-gray-600",
  flagged: "bg-red-100 text-red-700",
  auto_rejected: "bg-red-100 text-red-700",
};

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
}

export default function MembersAdmin() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/members").then(({ data }) => setMembers(data)).catch((e) => console.error(e)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, decision) => {
    try {
      await api.patch(`/admin/members/${id}`, null, { params: { decision } });
      toast.success(decision === "approved" ? "Member approved" : "Member rejected");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Could not update.")); }
  };

  const pending = members.filter((m) => m.status === "pending");
  const others = members.filter((m) => m.status !== "pending");

  const Card = ({ m }) => {
    const a = m.activity || {};
    return (
      <div className="bg-white border border-[#E5E5E5] p-6" data-testid={`member-${m.id}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-serif text-xl text-[#1A1F24]">{m.name}</div>
            <div className="text-sm text-[#5C6670] break-all">{m.email}</div>
          </div>
          <span className={`text-[0.6rem] tracking-[0.16em] uppercase px-2 py-1 rounded shrink-0 ${STATUS_STYLE[m.status] || "bg-gray-100 text-gray-600"}`} data-testid={`member-status-${m.id}`}>
            {m.status.replace("_", " ")}
          </span>
        </div>

        {m.flag_reason && (
          <div className="mt-3 flex items-center gap-2 text-xs text-red-600">
            <AlertTriangle className="w-3.5 h-3.5" /> {m.flag_reason}
          </div>
        )}

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-[#5C6670]">
          <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#C5A059]" /> {[m.city, m.region, m.country].filter(Boolean).join(", ") || "Unknown location"}</div>
          <div className="flex items-center gap-1.5"><Monitor className="w-3.5 h-3.5 text-[#C5A059]" /> {[m.browser, m.os].filter(Boolean).join(" · ") || "Unknown device"}</div>
          <div className="font-mono">{m.ip || "—"}</div>
          <div>Requested {fmt(m.created_at)}</div>
        </div>

        <div className="mt-3 flex items-center gap-2 text-xs">
          <History className="w-3.5 h-3.5 text-[#C5A059]" />
          {a.visited_earlier ? (
            <span className="text-[#5C6670]">
              Visited before · {a.visit_count} session{a.visit_count !== 1 ? "s" : ""}{a.last_seen ? `, last ${fmt(a.last_seen)}` : ""}
              {a.suspicious_before && <span className="text-red-600 font-medium"> · flagged earlier</span>}
            </span>
          ) : (
            <span className="text-[#B8BCC2]">No prior visits recorded</span>
          )}
        </div>
        {Array.isArray(a.pages) && a.pages.length > 0 && (
          <div className="mt-1.5 text-[0.7rem] text-[#9C9C94] font-mono break-all">{a.pages.join("  ›  ")}</div>
        )}

        {m.status === "pending" && (
          <div className="mt-5 flex gap-3">
            <button onClick={() => decide(m.id, "approved")} className="gv-btn-gold inline-flex items-center gap-1.5" data-testid={`member-approve-${m.id}`}>
              <Check className="w-4 h-4" /> Approve
            </button>
            <button onClick={() => decide(m.id, "rejected")} className="gv-btn-outline inline-flex items-center gap-1.5" data-testid={`member-reject-${m.id}`}>
              <X className="w-4 h-4" /> Reject
            </button>
          </div>
        )}
        {m.status === "rejected" && (
          <button onClick={() => decide(m.id, "approved")} className="mt-4 text-xs tracking-[0.18em] uppercase text-[#C5A059] gv-link" data-testid={`member-reapprove-${m.id}`}>Approve anyway</button>
        )}
        {m.status === "approved" && (
          <button onClick={() => decide(m.id, "rejected")} className="mt-4 text-xs tracking-[0.18em] uppercase text-[#5C6670] hover:text-red-500" data-testid={`member-revoke-${m.id}`}>Revoke access</button>
        )}
      </div>
    );
  };

  return (
    <AdminLayout title="Members">
      <Toaster position="top-center" />
      <p className="text-[#5C6670] font-light">Review requests for read-only access to your exclusive content. Suspicious IPs and fake emails are filtered out before they reach you.</p>

      {loading ? (
        <p className="mt-8 text-[#B8BCC2]">Loading…</p>
      ) : (
        <>
          <h2 className="mt-10 gv-label">Awaiting your decision ({pending.length})</h2>
          {pending.length === 0 ? (
            <p className="mt-3 text-sm text-[#5C6670] italic" data-testid="members-no-pending">No pending requests.</p>
          ) : (
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="members-pending">{pending.map((m) => <Card key={m.id} m={m} />)}</div>
          )}

          <h2 className="mt-12 gv-label">Decided & flagged ({others.length})</h2>
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="members-others">{others.map((m) => <Card key={m.id} m={m} />)}</div>
        </>
      )}
    </AdminLayout>
  );
}
